import styled, { css } from 'styled-components';
import { ListVariant } from '../../types/trade';
import { BidStatus } from '../../types/auction';
import Box from '../Box';
import isOmpPlatform from '../../utils/isOmpPlatform';

const isOmp = isOmpPlatform();

export const StyledFooterWrapper = styled.div`
  width: 100%;
  & > div {
    border-top: 2px solid ${({ theme }) => theme.COLORS.NEUTRAL_10};

    padding: 12px 18px;
    width: 100%;
  }

  & > div:first-child {
    border-top: 1px solid ${({ theme }) => theme.COLORS.NEUTRAL_10} !important;
  }
`;

const baseKebabMarginFromBorders = '16px';
const kebabMenuPositionVariants = {
  bottom: css`
    bottom: ${baseKebabMarginFromBorders};
  `,
  top: css`
    top: ${baseKebabMarginFromBorders};
  `
};

export const StyledKebabMenuWrapper = styled.div`
  position: absolute;
  right: ${baseKebabMarginFromBorders};
  ${({ position }) => kebabMenuPositionVariants[position]}
`;

export const StyledTitle = styled.a`
  && {
    font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
    font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
    padding: 12px 18px 10px;
    cursor: ${props => (props.hasActions ? 'pointer' : 'default')};
    color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
    text-decoration: none;

    &:hover,
    &:active,
    &:focus,
    &:visited {
      color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
    }
  }
`;

export const StyledContent = styled(Box.Column)`
  flex: 1 1 auto;
  padding: 0px 18px 18px;
`;

export const StyledFooter = styled(Box)`
  height: 37px;
  padding: 0 8px 0 18px;
  border-top: 2px solid ${({ theme }) => theme.COLORS.NEUTRAL_50};
  align-items: center;
`;

const StyledTradeCardVariants = {
  [ListVariant.LIST]: css`
    flex-direction: row;
    width: 100%;
    margin-bottom: 18px;
  `,
  [ListVariant.SEARCH]: css`
    flex-direction: row;
    width: 100%;
    margin-bottom: 18px;
  `,
  [ListVariant.LOADING]: css`
    flex-direction: row;
    width: 100%;
    margin-bottom: 18px;
  `,
  [ListVariant.GRID]: css`
    flex-direction: column;
    width: 236px;
  `,
  [ListVariant.PAYMENTS_ON_HOLD]: css`
    flex-direction: row;
    width: 100%;
    margin-bottom: 18px;
  `
};

const StyledTradeCardInSaleEvent = {
  true: css`
    margin-right: 12px;
    width: auto;
  `
};

const StyledTradeCardLastInSaleEvent = {
  true: css`
    margin-bottom: 0;
  `
};

const StyledContentContainerVariants = {
  [BidStatus.topBidder]: css`
    border-right: 6px solid ${({ theme }) => theme.COLORS.LA_PALMA};
  `,
  [BidStatus.proxy]: css`
    border-right: 6px solid ${({ theme }) => theme.COLORS.LA_PALMA};
  `,
  [BidStatus.outbid]: css`
    border-right: 6px solid ${({ theme }) => theme.COLORS.DANGER};
  `
};

const StyledThumbnailVariants = {
  [ListVariant.LIST]: css`
    margin: 0;
    border-start-start-radius: 3px;
    border-end-start-radius: 3px;
  `,
  [ListVariant.SEARCH]: css`
    margin-top: 12px;
    border-radius: 3px;
  `
};

export const StyledTradeCard = styled.div`
  background: ${({ theme }) => theme.COLORS.BASE};
  position: relative;
  cursor: auto;
  display: flex;
  flex-direction: column;

  flex-wrap: ${({ variant }) =>
    variant === ListVariant.LIST ? 'nowrap' : 'wrap'};

  ${({ variant }) => StyledTradeCardVariants[variant]}

  ${props => StyledTradeCardInSaleEvent[props.isInSaleEvent]}

  ${props => StyledTradeCardLastInSaleEvent[props.isLastTradeInSaleEvent]}

  ${() =>
    !isOmp &&
    css`
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    `}

  ${() =>
    isOmp &&
    css`
      border: ${({ theme }) => `1px solid ${theme.COLORS.NEUTRAL_110}`};
      border-radius: 6px;
    `}

  .selected {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 56%, #000000);
    color: ${({ theme }) => theme.COLORS.BASE};
    z-index: 1;
    display: flex;
    padding: 12px;
    font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
    font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
    align-items: flex-end;
    letter-spacing: 1.54px;
  }
`;

export const StyledContentContainer = styled(Box)`
  position: relative;
  height: ${({ height }) => height || '204'}px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  max-width: ${props => (props.isInSaleEvent ? '622px' : '642px')};

  &:hover,
  &:active,
  &:focus,
  &:visited {
    color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  }

  ${({ bidStatus }) => StyledContentContainerVariants[bidStatus]}
`;

export const StyledThumbnail = styled.a`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  margin-left: 12px;
  margin-bottom: 12px;
  width: ${({ width }) => width || '240'}px;
  height: ${({ height }) => height || '204'}px;
  cursor: ${props => (props.hasActions ? 'pointer' : 'default')};
  background: url(${props => (props.url ? props.url : 'none')}) no-repeat;
  background-position: top;
  background-size: cover;
  background-color: ${({ theme }) => theme.COLORS.NEUTRAL_50};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};

  &:hover,
  &:active,
  &:focus,
  &:visited {
    color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  ${({ variant }) => StyledThumbnailVariants[variant]}
  border-end-start-radius: ${props => props.hasFooters && 'unset'};
`;

export const StyledActiveListTimer = styled.div`
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  align-self: flex-end;
  padding: 4px 9px;
  display: ${props => props.isTimerExtended && 'inline-flex'};
  border-radius: ${({ isTimerExtended }) =>
    isTimerExtended ? '3px 0 0 3px' : '3px'};
  background-color: ${({ theme, isTimerExtended }) =>
    !isTimerExtended && theme.COLORS.BASE};
  z-index: 1;
  position: absolute;
  top: ${({ isTimerExtended }) => (isTimerExtended ? '2px' : '6px')};
  left: ${({ isTimerExtended }) => (isTimerExtended ? '-2px' : '6px')};
`;
